import type { PluginOnsiteEditType } from "@intelligems/ig-types";

/**
 * Type for onsite edit objects that support grouping.
 * Allows the utility to work with both OnsiteEditDto and PluginOnsiteEditType.
 */
export type OnsiteEditLike = {
  find: string | null;
  isRegex: boolean;
  querySelectors: string[];
  imageSearchUrl?: string | null;
  groupId?: string;
};

/**
 * Creates the composite key used for grouping onsite edits.
 *
 * @param onsiteEdit - The onsite edit to create a key for
 * @returns A deterministic string key based on semantic properties
 */
export function createOnsiteEditGroupKey(
  onsiteEdit: Pick<
    OnsiteEditLike,
    "find" | "isRegex" | "querySelectors" | "imageSearchUrl"
  >
): string {
  const parts = [
    String(onsiteEdit.find || ""),
    String(onsiteEdit.isRegex),
    ...onsiteEdit.querySelectors,
    String(onsiteEdit.imageSearchUrl || ""),
  ];
  return parts.join("|");
}

/**
 * Computes or assigns groupIds to onsite edits based on their semantic properties.
 * Edits with identical find, isRegex, and querySelectors will receive the same groupId.
 *
 * @param onsiteEdits - Array of onsite edits to process
 * @param generateId - Callback function to generate new unique IDs
 * @param onsiteEditGroups - Optional existing groupId map to preserve existing groups
 * @returns The same array with groupId properties assigned
 */
export function assignOnsiteEditGroupIds<T extends OnsiteEditLike>(
  onsiteEdits: T[],
  generateId: () => string,
  onsiteEditGroups: Map<string, string> = new Map()
): T[] {
  onsiteEdits?.forEach((onsiteEdit) => {
    const groupKey = createOnsiteEditGroupKey(onsiteEdit);

    if (onsiteEdit.groupId) {
      // Already has a groupId, register it
      onsiteEditGroups.set(groupKey, onsiteEdit.groupId);
    }

    if (onsiteEditGroups.has(groupKey)) {
      // Reuse existing groupId for this semantic key
      onsiteEdit.groupId = onsiteEditGroups.get(groupKey);
    } else {
      // Generate new groupId using provided callback
      const groupId = generateId();
      onsiteEdit.groupId = groupId;
      onsiteEditGroups.set(groupKey, groupId);
    }
  });

  return onsiteEdits;
}

export function checkOnsiteEdit(
  onsiteEdit: PluginOnsiteEditType,
  attr: "html" | "image" | "text" | "hide" | "skip"
): boolean {
  switch (attr) {
    case "html":
      return onsiteEdit.isHtml || onsiteEdit.onsiteEditType === "html";
    case "image":
      return onsiteEdit.isImage || onsiteEdit.onsiteEditType === "image";
    case "text":
      return (
        !checkOnsiteEdit(onsiteEdit, "html") &&
        !checkOnsiteEdit(onsiteEdit, "image") &&
        onsiteEdit.onsiteEditType !== "hide"
      );
    case "hide":
      return onsiteEdit.onsiteEditType === "hide" || onsiteEdit.remove;
    case "skip":
      return onsiteEdit.skip;
  }
}
